import { getLogger } from '@/idv/utils';
import type { CompletePayload, RNWebViewAdapterReturn } from '../types';
import generateEventEmitter from '../utils/generate-event-emitter';
import { setClientVersionHeaderFromUrl } from './set-client-version-header';

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(message: string): void };
  }
}

type RNWebViewEvent = Event & {
  detail: {
    command: string;
  };
};

const { logInfo } = getLogger({ location: 'rn-webview-adapter' });

const RN_WEBVIEW_POST_MESSAGE_TYPE = 'footprint_rn_webview_message';

const isRNWebViewEvent = (event: Event): event is RNWebViewEvent => {
  return (
    'detail' in event &&
    typeof event.detail === 'object' &&
    event.detail !== null &&
    'command' in event.detail &&
    typeof event.detail.command === 'string'
  );
};

const generateRNWebViewAdapter = (): RNWebViewAdapterReturn => {
  let isAdapterLoaded = false;
  const eventEmitter = generateEventEmitter();
  setClientVersionHeaderFromUrl();

  const load = (): Promise<void> => {
    if (isAdapterLoaded) {
      return Promise.resolve();
    }
    isAdapterLoaded = true;
    logInfo('Loading footprint from RN web view adapter');
    window.addEventListener(RN_WEBVIEW_POST_MESSAGE_TYPE, event => {
      if (isRNWebViewEvent(event)) {
        eventEmitter.emit(event.detail.command);
      }
    });
    return Promise.resolve();
  };

  const close = (): void => {
    logInfo('Closing footprint from RN web view adapter');
    window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'closed' }));
  };

  const cancel = (): void => {
    logInfo('Canceling footprint from RN web view adapter');
    window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'canceled' }));
  };

  const complete = ({ validationToken, delay = 0, autoClose = true }: CompletePayload): void => {
    logInfo('Completing footprint from RN web view adapter');
    window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'completed', validation_token: validationToken }));
    if (!autoClose) {
      return;
    }
    setTimeout(() => {
      logInfo('Closing footprint after complete timeout from RN web view adapter');
      window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'closed' }));
    }, delay);
  };

  const backButtonVisibilityChange = (isVisible: boolean): void => {
    logInfo('Back button visibility changed from RN web view adapter');
    window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'backButtonVisibilityChange', isVisible }));
  };

  return {
    auth: () => undefined,
    relayToComponents: () => undefined,
    backButtonVisibilityChange,
    requirementChanged: () => {},
    resized: () => {},
    load,
    cancel,
    close,
    complete,
    on: eventEmitter.on,
  };
};

export default generateRNWebViewAdapter;
